/**
 * Minimal AppSync client for the Agent Builder Copilot API.
 *
 * The platform's Amplify API module is configured globally for the pubsub API (runtime
 * debugger, live chat). The copilot is a separate AppSync API, so it gets its own small
 * client instead of reconfiguring Amplify:
 *   - queries / mutations: HTTPS POST with the Lambda-authorizer token
 *   - subscriptions: the AppSync realtime WebSocket protocol (graphql-ws subprotocol)
 *     https://docs.aws.amazon.com/appsync/latest/devguide/real-time-websocket-client.html
 */

const toBase64 = value => window.btoa(unescape(encodeURIComponent(JSON.stringify(value))));

/** Call a consumer callback; a bug in the consumer must not kill the socket handler. */
function safeCall(fn, ...args) {
    if (typeof fn !== 'function') return;
    try {
        fn(...args);
    } catch (error) {
        console.error('[copilot] subscription callback failed', error);
    }
}

/** Realtime endpoint and the host the authorization header must name. */
export function realtimeEndpoint(httpUrl) {
    const url = new URL(httpUrl);
    // Default domain: <id>.appsync-api.<region>.amazonaws.com -> <id>.appsync-realtime-api.<region>.amazonaws.com
    if (url.host.includes('.appsync-api.')) {
        return {
            host: url.host,
            url: `wss://${url.host.replace('.appsync-api.', '.appsync-realtime-api.')}${url.pathname}`,
        };
    }
    // Custom domain: wss://<domain>/graphql/realtime
    return { host: url.host, url: `wss://${url.host}${url.pathname.replace(/\/$/, '')}/realtime` };
}

export class CopilotApiError extends Error {}

export async function graphqlRequest(httpUrl, authToken, query, variables) {
    let response;
    try {
        response = await fetch(httpUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: authToken },
            body: JSON.stringify({ query, variables }),
        });
    } catch (err) {
        throw new CopilotApiError('Could not reach the copilot service. Check your connection and try again.');
    }
    const body = await response.json().catch(() => ({}));
    if (response.status === 401 || response.status === 403) {
        throw new CopilotApiError('The copilot is not authorised for this agent.');
    }
    if (!response.ok || body.errors?.length) {
        const message = body.errors?.[0]?.message || `Copilot request failed (${response.status}).`;
        throw new CopilotApiError(message);
    }
    return body.data && typeof body.data === 'object' ? body.data : {};
}

/**
 * Subscribe over the AppSync realtime protocol. Reconnects with backoff when the socket
 * drops or keep-alives stop. Returns { ready: Promise, close() }; `ready` resolves once the
 * subscription is registered, so a caller can subscribe before sending its first message.
 */
export function subscribe(httpUrl, authToken, query, variables, { onData, onError, onStatus } = {}) {
    let endpoint;
    try {
        endpoint = realtimeEndpoint(httpUrl);
    } catch (_) {
        const error = new CopilotApiError('The copilot endpoint is not configured correctly.');
        safeCall(onError, error);
        const ready = Promise.reject(error);
        ready.catch(() => {}); // awaiting callers still see the rejection; nobody else must
        return { ready, close() {} };
    }
    const { host, url } = endpoint;
    const authorization = { host, Authorization: authToken };
    const subscriptionId = (window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`).toString();

    let socket = null;
    let closed = false;
    let attempt = 0;
    let keepAliveTimer = null;
    let keepAliveMs = 300000;
    let resolveReady;
    let rejectReady;
    const ready = new Promise((resolve, reject) => { resolveReady = resolve; rejectReady = reject; });
    ready.catch(() => {});

    const armKeepAlive = () => {
        clearTimeout(keepAliveTimer);
        keepAliveTimer = setTimeout(() => socket && socket.close(), keepAliveMs);
    };

    const fail = error => {
        closed = true;
        clearTimeout(keepAliveTimer);
        rejectReady(error);
        safeCall(onError, error);
        safeCall(onStatus, 'closed');
    };

    const connect = () => {
        if (closed) return;
        safeCall(onStatus, attempt === 0 ? 'connecting' : 'reconnecting');
        try {
            socket = new WebSocket(`${url}?header=${toBase64(authorization)}&payload=${toBase64({})}`, ['graphql-ws']);
        } catch (_) {
            // Invalid URL, blocked by CSP, or WebSockets unavailable: nothing to retry.
            fail(new CopilotApiError("Couldn't open a connection to the copilot."));
            return;
        }

        socket.onopen = () => {
            try {
                socket.send(JSON.stringify({ type: 'connection_init' }));
            } catch (_) {
                socket.close();
            }
        };

        socket.onerror = () => {
            // onclose follows and handles the retry; nothing may escape to window.onerror.
        };

        socket.onmessage = event => {
            let message;
            try {
                message = JSON.parse(event.data);
            } catch (_) {
                return;
            }
            switch (message.type) {
                case 'connection_ack':
                    keepAliveMs = message.payload?.connectionTimeoutMs || keepAliveMs;
                    armKeepAlive();
                    socket.send(JSON.stringify({
                        id: subscriptionId,
                        type: 'start',
                        payload: {
                            data: JSON.stringify({ query, variables }),
                            extensions: { authorization },
                        },
                    }));
                    break;
                case 'start_ack':
                    attempt = 0;
                    safeCall(onStatus, 'connected');
                    resolveReady();
                    break;
                case 'ka':
                    armKeepAlive();
                    break;
                case 'data':
                    armKeepAlive();
                    safeCall(onData, message.payload?.data);
                    break;
                case 'error':
                case 'connection_error': {
                    const detail = message.payload?.errors?.[0];
                    const error = new CopilotApiError(
                        detail?.errorType === 'UnauthorizedException' || /unauthori[sz]ed/i.test(detail?.message || '')
                            ? 'The copilot is not authorised for this agent.'
                            : detail?.message || 'The copilot connection failed.');
                    fail(error);
                    try {
                        socket.close();
                    } catch (_) {
                        // already closed
                    }
                    break;
                }
                default:
                    break;
            }
        };

        socket.onclose = () => {
            clearTimeout(keepAliveTimer);
            if (closed) {
                safeCall(onStatus, 'closed');
                return;
            }
            attempt += 1;
            const delay = Math.min(30000, 1000 * 2 ** Math.min(attempt, 5));
            safeCall(onStatus, 'reconnecting');
            setTimeout(connect, delay);
        };
    };

    connect();

    return {
        ready,
        close() {
            closed = true;
            clearTimeout(keepAliveTimer);
            try {
                if (socket && socket.readyState === WebSocket.OPEN) {
                    socket.send(JSON.stringify({ id: subscriptionId, type: 'stop' }));
                }
                socket && socket.close();
            } catch (_) {
                // already closed
            }
        },
    };
}
